// Shared bilingual page primitives for /ar/* pages.
//
// These wrap the site's existing InnerBlock / GridCards / PageHero building
// blocks so Arabic pages read as ordinary React components instead of a big
// dictionary of strings. Latin brand names are written as English inline
// (Wafa Solutions, Meyar, Altalib Group, etc.), matching the owner's rule.
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { WhatsAppButton } from "@/components/WhatsAppButton";

export function ArPageHero({ eyebrow, h1, sub, primaryCta, secondaryCta, breadcrumbs }) {
  return (
    <section className="bg-wafa-cream/40" data-testid="ar-page-hero">
      <div className="mx-auto max-w-7xl px-4 py-16 lg:px-6 lg:py-24">
        {breadcrumbs ? (
          <nav className="mb-6 text-xs text-wafa-ink/60">
            {breadcrumbs.map((b, i) => (
              <span key={i}>
                {i > 0 ? <span className="mx-1">/</span> : null}
                {b.to ? <Link to={b.to} className="hover:text-wafa-deep">{b.label}</Link> : <span>{b.label}</span>}
              </span>
            ))}
          </nav>
        ) : null}
        {eyebrow ? (
          <div className="text-xs font-semibold uppercase tracking-widest text-wafa-primary">{eyebrow}</div>
        ) : null}
        <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
          {h1}
        </h1>
        {sub ? (
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-wafa-ink/85 lg:text-lg">{sub}</p>
        ) : null}
        {(primaryCta || secondaryCta) ? (
          <div className="mt-8 flex flex-wrap gap-3">
            {primaryCta ? (
              <Link to={primaryCta.to} className="inline-flex items-center gap-1.5 rounded-md bg-wafa-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-wafa-deep">
                <ArrowLeft className="h-4 w-4" /> {primaryCta.label}
              </Link>
            ) : null}
            {secondaryCta ? (
              <Link to={secondaryCta.to} className="inline-flex items-center gap-1.5 rounded-md border border-wafa-deep/20 px-5 py-2.5 text-sm font-semibold text-wafa-deep hover:border-wafa-deep">
                {secondaryCta.label}
              </Link>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function ArBlock({ tint = "white", heading, children }) {
  const bg = tint === "cream" ? "bg-wafa-cream/40" : "bg-white";
  return (
    <section className={bg}>
      <div className="mx-auto max-w-7xl px-4 py-16 lg:px-6 lg:py-24">
        {heading ? (
          <h2 className="font-heading text-2xl font-semibold tracking-tight text-wafa-deep sm:text-3xl lg:text-4xl">
            {heading}
          </h2>
        ) : null}
        <div className="mt-8">{children}</div>
      </div>
    </section>
  );
}

export function ArGrid({ items, columns = 3 }) {
  const grid = columns === 4 ? "lg:grid-cols-4" : columns === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3";
  return (
    <div className={`grid grid-cols-1 gap-6 sm:grid-cols-2 ${grid}`}>
      {items.map((it, i) => (
        <div key={i} className="rounded-lg border border-wafa-deep/10 bg-white p-6">
          <div className="font-heading text-lg font-semibold text-wafa-deep">{it.title}</div>
          <p className="mt-3 text-sm leading-relaxed text-wafa-ink/80">{it.desc}</p>
        </div>
      ))}
    </div>
  );
}

export function ArBullets({ items }) {
  return (
    <ul className="space-y-3">
      {items.map((t, i) => (
        <li key={i} className="flex gap-3 text-base leading-relaxed text-wafa-ink/85">
          <span aria-hidden className="mt-2 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-wafa-primary" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

export function ArContactCTA({ heading = "جاهزون؟ أخبرونا بما يُبطئ أعمالكم.", serviceWhatsApp = false }) {
  return (
    <section className="bg-wafa-deep text-white">
      <div className="mx-auto max-w-7xl px-4 py-16 lg:px-6 lg:py-20">
        <h2 className="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">{heading}</h2>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/ar/company/contact" className="inline-flex items-center gap-1.5 rounded-md bg-wafa-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-wafa-sunshine hover:text-wafa-deep">
            <ArrowLeft className="h-4 w-4" /> احجزوا مكالمة استكشافية
          </Link>
          <a href="mailto:support@wafa.om" className="inline-flex items-center gap-1.5 rounded-md border border-white/25 px-5 py-2.5 text-sm font-semibold text-white hover:border-white">
            support@wafa.om
          </a>
          {serviceWhatsApp ? (
            <WhatsAppButton variant="whatsapp" label="اسأل عن هذه الخدمة عبر واتساب" source="service_bottom" testId="service-whatsapp-bottom" />
          ) : (
            <WhatsAppButton className="text-sm" label="تحدث معنا عبر واتساب" source="page_cta" />
          )}
        </div>
      </div>
    </section>
  );
}

export const WHAT_HAPPENS_NEXT_AR = [
  "مكالمة لمدة ثلاثين دقيقة مع مهندس. لا فريق مبيعات.",
  "ننظر في طريقة عملكم الحالية، وأين يُكلّفكم النظام وقتًا أو مالًا.",
  "نحدد المكاسب السريعة بمعزل عن البناء الأكبر، لتروا الفرق بوضوح.",
  "تحصلون على ملخص من صفحة واحدة: النطاق، المخاطر، نطاق تكلفة حقيقي، وجدول زمني.",
  "الملخص لكم في الحالتين. خذوه لمورد آخر إن أردتم. بلا ضغط، وبلا حملة متابعة.",
];

export function ArWhatHappensNext() {
  return (
    <ArBlock tint="white" heading="ماذا يحدث بعد ذلك">
      <ol className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {WHAT_HAPPENS_NEXT_AR.map((text, i) => (
          <li key={i} className="rounded-2xl bg-wafa-cream/70 p-6">
            <div className="font-heading text-4xl font-bold leading-none text-wafa-primary">{i + 1}</div>
            <p className="mt-4 text-sm leading-relaxed text-wafa-deep/85">{text}</p>
          </li>
        ))}
      </ol>
    </ArBlock>
  );
}
